'use client';

import { useRouter } from 'next/navigation';
import { signIn, useSession } from 'next-auth/react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Logo } from '@/lib/ui/Logo';
import { useIsMobile } from '@/lib/ui/useIsMobile';
import { checkRoom, extractRoomCode } from '@/lib/meetClient';

export default function LoginPage() {
  const router = useRouter();
  const isMobile = useIsMobile();
  const [code, setCode] = useState('');
  const [joining, setJoining] = useState(false);
  const { status } = useSession();

  // Already signed in → straight to Home.
  useEffect(() => {
    if (status === 'authenticated') router.replace('/');
  }, [status, router]);

  const join = async () => {
    if (joining) return;
    const parsed = extractRoomCode(code);
    if (!parsed) {
      toast.error('Enter a valid code or link (e.g. abc-defg-hij)');
      return;
    }
    setJoining(true);
    try {
      // Tell the guest up front when the room doesn't exist rather than dropping
      // them into an endless knock/wait. Fail open on a transient server error.
      const exists = await checkRoom(parsed);
      if (exists === 'not-found') {
        toast.error(`No meeting found for “${parsed}”. Check the code and try again.`);
        return;
      }
      router.push(`/rooms/${parsed}`);
    } finally {
      setJoining(false);
    }
  };

  const continueWithGoogle = () => signIn('google', { callbackUrl: '/' });

  return (
    <div style={{ ...S.page, ...(isMobile ? SM.page : null) }}>
      <div style={{ ...S.card, ...(isMobile ? SM.card : null) }}>
        {/* left brand panel */}
        <div style={{ ...S.brandPanel, ...(isMobile ? SM.brandPanel : null) }}>
          <div style={S.blob1} />
          <div style={S.blob2} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
            <Logo height={isMobile ? 34 : 40} />
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ ...S.brandTitle, ...(isMobile ? SM.brandTitle : null) }}>
              Meetings that just start.
            </div>
            {!isMobile && (
              <div style={S.brandSub}>
                Sign in to create a room in one click — or join any link as a guest, no account
                needed.
              </div>
            )}
          </div>
          {!isMobile && (
            <div style={S.brandFoot}>
              <span>Encrypted media</span>
              <span>·</span>
              <span>No downloads</span>
              <span>·</span>
              <span>Guest-friendly</span>
            </div>
          )}
        </div>

        {/* right form */}
        <div style={{ ...S.formSide, ...(isMobile ? SM.formSide : null) }}>
          <div style={{ width: isMobile ? '100%' : 392 }}>
            <div style={S.h1}>Sign in</div>
            <div style={S.sub}>Use your Google account to host and manage meetings.</div>

            <button style={S.googleBtn} onClick={continueWithGoogle}>
              <GoogleIcon />
              Continue with Google
            </button>

            <div style={S.divider}>
              <div style={S.line} />
              <span style={S.dividerText}>Just joining?</span>
              <div style={S.line} />
            </div>

            {/* prominent guest path */}
            <div style={S.guestCard}>
              <div style={{ font: "700 14px/1 'Plus Jakarta Sans'", color: '#0e6b47', marginBottom: 12 }}>
                Have a meeting link or code?
              </div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
                <input
                  placeholder="abc-defg-hij"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && join()}
                  style={S.guestInput}
                />
                <button
                  style={{
                    ...S.guestBtn,
                    ...(isMobile ? { width: '100%' } : null),
                    ...(joining ? { opacity: 0.6, cursor: 'default' } : null),
                  }}
                  onClick={join}
                  disabled={joining}
                >
                  {joining ? 'Checking…' : isMobile ? 'Join as guest' : 'Join'}
                </button>
              </div>
              <div style={{ font: "400 12.5px/1.5 'Plus Jakarta Sans'", color: '#4f7d67', marginTop: 10 }}>
                No sign-in required — just enter your name on the next screen.
              </div>
            </div>

            <div style={S.terms}>By continuing you agree to the Terms &amp; Privacy Policy.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'grid',
    placeItems: 'center',
    background: '#e9eaee',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    padding: 24,
  },
  card: {
    width: 'min(1040px, 100%)',
    minHeight: 620,
    background: '#f4f5f7',
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 30px 70px -30px rgba(20,24,40,.4), 0 2px 8px rgba(20,24,40,.08)',
    display: 'flex',
  },
  brandPanel: {
    width: 460,
    flex: 'none',
    background: 'linear-gradient(165deg,#16213f 0%,#1d2c54 55%,#24408a 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '56px 52px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: '#eef1f8',
  },
  blob1: {
    position: 'absolute',
    width: 520,
    height: 520,
    right: -180,
    top: -140,
    borderRadius: '50%',
    background: 'radial-gradient(circle at 30% 30%,rgba(88,140,255,.55),transparent 62%)',
    filter: 'blur(6px)',
  },
  blob2: {
    position: 'absolute',
    width: 360,
    height: 360,
    left: -120,
    bottom: -120,
    borderRadius: '50%',
    background: 'radial-gradient(circle at 50% 50%,rgba(63,214,177,.28),transparent 65%)',
  },
  brandTitle: {
    font: "800 40px/1.12 'Plus Jakarta Sans'",
    letterSpacing: '-.02em',
    marginBottom: 18,
    maxWidth: 420,
  },
  brandSub: {
    font: "400 16px/1.6 'Plus Jakarta Sans'",
    color: '#b9c3de',
    maxWidth: 380,
  },
  brandFoot: {
    position: 'relative',
    display: 'flex',
    gap: 26,
    font: "500 13px/1 'Plus Jakarta Sans'",
    color: '#9fabca',
  },
  formSide: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  h1: {
    font: "700 27px/1.2 'Plus Jakarta Sans'",
    letterSpacing: '-.02em',
    color: '#16181d',
  },
  sub: {
    font: "400 15px/1.5 'Plus Jakarta Sans'",
    color: '#5b616e',
    marginTop: 8,
  },
  googleBtn: {
    marginTop: 28,
    width: '100%',
    height: 52,
    border: '1px solid #d7dae1',
    background: '#fff',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    cursor: 'pointer',
    font: "600 15px/1 'Plus Jakarta Sans'",
    color: '#16181d',
    boxShadow: '0 1px 2px rgba(20,24,40,.05)',
  },
  divider: { display: 'flex', alignItems: 'center', gap: 14, margin: '26px 0' },
  line: { flex: 1, height: 1, background: '#e5e7eb' },
  dividerText: {
    font: "600 12px/1 'Plus Jakarta Sans'",
    letterSpacing: '.08em',
    color: '#9aa0ac',
    textTransform: 'uppercase',
  },
  guestCard: {
    background: '#eef7f2',
    border: '1px solid #c9e8d8',
    borderRadius: 14,
    padding: 18,
  },
  guestInput: {
    flex: 1,
    height: 46,
    border: '1px solid #b7ddc9',
    borderRadius: 10,
    padding: '0 14px',
    font: "600 15px/1 'JetBrains Mono'",
    color: '#16181d',
    background: '#fff',
    outline: 'none',
  },
  guestBtn: {
    height: 46,
    padding: '0 20px',
    border: 'none',
    background: '#12a06a',
    color: '#fff',
    borderRadius: 10,
    font: "700 14px/1 'Plus Jakarta Sans'",
    cursor: 'pointer',
  },
  terms: {
    font: "400 12px/1.6 'Plus Jakarta Sans'",
    color: '#9aa0ac',
    marginTop: 26,
    textAlign: 'center',
  },
};

/* mobile overrides — stack the brand hero above the form (see mockup M1) */
const SM: Record<string, React.CSSProperties> = {
  page: { padding: 0, placeItems: 'stretch' },
  card: {
    width: '100%',
    minHeight: '100vh',
    borderRadius: 0,
    boxShadow: 'none',
    flexDirection: 'column',
  },
  brandPanel: {
    width: '100%',
    flex: 'none',
    padding: '64px 30px 34px',
    justifyContent: 'flex-end',
    gap: 24,
  },
  brandTitle: { font: "800 27px/1.15 'Plus Jakarta Sans'", marginBottom: 0, maxWidth: 320 },
  formSide: { alignItems: 'flex-start', padding: '30px 26px 40px' },
};
